// src/hooks/useNoiseSuppression.js
// Manages the Web Audio graph for RNNoise-based noise suppression.
//
// Audio graph:
//   rawMicStream
//     → MediaStreamAudioSourceNode
//     → AudioWorkletNode('rnnoise-processor')   [toggle-able bypass]
//     → MediaStreamAudioDestinationNode
//     → processedStream  (returned to caller for use in WebRTC)
//
// The processed stream track is kept alive for the entire voice session.
// Toggle (on/off) sends a message to the worklet — no track replacement,
// no renegotiation.

import { useRef, useCallback, useEffect } from 'react';

const WORKLET_URL = '/rnnoise-processor.js'; // served from public/

export function useNoiseSuppression() {
  const audioCtxRef = useRef(null);
  const workletRef  = useRef(null);
  const sourceRef   = useRef(null);
  const destRef     = useRef(null);
  const enabledRef  = useRef(true);
  const onVadRef    = useRef(null);

  // ── Setup ──────────────────────────────────────────────────────────────────
  // Takes the raw mic MediaStream, builds the audio graph, returns the
  // processed MediaStream to feed into WebRTC.
  const setup = useCallback(async (rawStream, { enabled = true, onVad } = {}) => {
    _teardownGraph();

    enabledRef.current = enabled;
    onVadRef.current   = onVad ?? null;

    // Force 48 kHz so RNNoise frames are exactly 480 samples = 10 ms.
    // Safari may run its internal graph at 44.1 kHz regardless; the worklet's
    // ring buffer handles the mismatch correctly (no pitch shift, no speed change).
    let ctx;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate:  48000,
        latencyHint: 'interactive',
      });
    } catch (_) {
      // Some environments reject forced sampleRate — fall back without it.
      ctx = new (window.AudioContext || window.webkitAudioContext)({
        latencyHint: 'interactive',
      });
    }

    // AudioContext starts in 'suspended' on Safari until a user gesture resumes it.
    // setup() is called from joinVoice(), which is inside a click handler, so this
    // resume() call is already within a user gesture.
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    audioCtxRef.current = ctx;

    const source = ctx.createMediaStreamSource(rawStream);
    const dest   = ctx.createMediaStreamDestination();
    sourceRef.current = source;
    destRef.current   = dest;

    // ── Try AudioWorklet ───────────────────────────────────────────────────
    let workletOk = false;
    try {
      await ctx.audioWorklet.addModule(WORKLET_URL);

      const worklet = new AudioWorkletNode(ctx, 'rnnoise-processor', {
        numberOfInputs:     1,
        numberOfOutputs:    1,
        outputChannelCount: [1],
      });

      // Set initial enabled state before first quantum is processed
      worklet.port.postMessage({ type: 'toggle', enabled: enabledRef.current });

      worklet.port.onmessage = ({ data }) => {
        switch (data.type) {
          case 'vad':
            if (onVadRef.current) onVadRef.current(data.prob);
            break;
          case 'error':
            // WASM load failed — worklet continues in pass-through mode
            console.warn('[rnnoise] WASM init failed, running in pass-through:', data.message);
            break;
          case 'ready':
            // WASM fully initialized — noise suppression is now active
            break;
          default:
            break;
        }
      };

      source.connect(worklet);
      worklet.connect(dest);
      workletRef.current = worklet;
      workletOk = true;
    } catch (err) {
      // AudioWorklet not supported (old Safari, non-secure context, etc.)
      // Fall through to direct pass-through — browser NS is the only processing.
      console.warn('[useNoiseSuppression] AudioWorklet unavailable, using pass-through:', err);
    }

    if (!workletOk) {
      source.connect(dest);
    }

    return dest.stream;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Toggle NS on/off without any WebRTC renegotiation ─────────────────────
  // The worklet's process() simply bypasses RNNoise when disabled,
  // so the outgoing track stays the same object throughout the session.
  const setEnabled = useCallback((enabled) => {
    enabledRef.current = enabled;
    workletRef.current?.port.postMessage({ type: 'toggle', enabled });
  }, []);

  // ── Teardown ───────────────────────────────────────────────────────────────
  function _teardownGraph() {
    try {
      sourceRef.current?.disconnect();
      workletRef.current?.disconnect();
      workletRef.current?.port.close();
      // Do NOT close destRef — MediaStreamAudioDestinationNode auto-cleans on ctx.close()
      audioCtxRef.current?.close();
    } catch (_) {}

    audioCtxRef.current = null;
    workletRef.current  = null;
    sourceRef.current   = null;
    destRef.current     = null;
  }

  const teardown = useCallback(() => {
    _teardownGraph();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Automatically tear down when the component unmounts
  useEffect(() => () => _teardownGraph(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return { setup, setEnabled, teardown };
}
